import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  ButtonDropdown,
  ButtonDropdownProps,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import {
  TeamChallengeProgress,
  TeamChallengeTaskProgress,
  TeamProgressResponse,
} from '@/src/types/JamTeamChallengeAnswer';
import ShowTasks from './ShowTasks';
import { getTaskScore } from '@/src/utils/task-score.utils';
import LeaderboardAdjustments, { LeaderboardAdjustmentChallenge } from './LeaderboardAdjustments';
import { config } from '@/src/config/app-config';
import { CancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import { useApi } from '@/src/store/api.context';
import { downloadAsPEM } from '@/src/utils/download.utils';
import { ConfirmModal } from '@/src/components/common/ConfirmModal';
import { preProdLogger } from '@/src/utils/log.utils';
import AwsCliModal from '@/src/components/ui/molecules/MyJams/JamChallengeDetails/JamOverview/Modals/AwsCLI/AwsCLIModal';
import { AccountCredentials, Lab } from '@/src/types/LabModels';
import { useJamFacilitator } from '@/src/store/jam-facilitator.context';
import { useAsyncEffect } from '@/src/hooks/useAsyncEffect';
import { max, sortBy, sumBy } from 'lodash';

interface TeamChallengeProgressAnswerProps {
  challenge: TeamChallengeProgress;
}

enum TEAM_CHALLENGE_DROPDOWN_ITEMS {
  RESTART_CHALLENGE = 'restart-challenge',
  MARK_TASK_AS_COMPLETED = 'mark-task-as-completed',
  DOWNLOAD_SSH_KEY_PAIR = 'download-ssh-keypair',
  LOGIN_AS_TEAM_ROLE = 'login-as-team-role',
  LOGIN_AS_ADMIN_ROLE = 'login-as-admin-role',
  TEAM_ROLE_CLI_ACCESS = 'team-role-cli-access',
  ADMIN_ROLE_CLI_ACCESS = 'admin-role-cli-access',
  REFUND_TASK_CLUE = 'refund-task-clue',
}

enum TEAM_CHALLENGE_CONFIRM_MODAL_TYPE {
  RESTART_CHALLENGE_MODAL = 'restart-challenge-modal',
  MARK_TASK_COMPLETED = 'mark-task-completed',
  REFUND_TASK_CLUE = 'refund-task-clue',
}

const formatTime = (value: number | null): string => {
  if (value == null) {
    return '--';
  }

  return new Date(value).toLocaleTimeString([], { timeZoneName: 'short' });
};

const toCompletionTimestamp = (task: TeamChallengeTaskProgress): number | null => {
  if (task.completedTime == null) {
    return null;
  }

  if (typeof task.completedTime === 'string') {
    return new Date(task.completedTime).valueOf();
  }

  return task.completedTime;
};

const getMostRecentCompletionTime = (taskProgress: TeamChallengeTaskProgress[]): number | null => {
  const completionTimes = (taskProgress || [])
    .map(toCompletionTimestamp)
    .filter((timestamp): timestamp is number => timestamp != null);

  return max(completionTimes) ?? null;
};

const sumUnrefundedCluePenalty = (taskProgress: TeamChallengeTaskProgress[]): number => {
  const unrefundedPenalties = (taskProgress || [])
    .flatMap((task) => task.cluePenalties || [])
    .filter((cluePenalty) => !cluePenalty.refunded);

  return sumBy(unrefundedPenalties, (cluePenalty) => cluePenalty.penalty ?? 0);
};

const getMostRecentRestartTimestamp = (labs: Lab[]): number | null => {
  const restartTimes = labs
    .filter((lab) => lab.status === 'RESTARTED')
    .map((lab) => lab.statusHistory.find((item) => item.status === 'RESTARTED')?.time)
    .filter((time): time is number => time != null);

  return max(restartTimes) ?? null;
};

const sumTaskField = (taskProgress: TeamChallengeTaskProgress[], field: 'pointsEarned' | 'pointsPossible'): number => {
  return sumBy(taskProgress, (task) => task[field] ?? 0);
};

const countFullyCompletedTasks = (taskProgress: TeamChallengeTaskProgress[]): number => {
  const fullyCompleted = taskProgress.filter((task) => task.pointsEarned === task.pointsPossible);

  return fullyCompleted.length;
};

const computeChallengeStats = (challenge: TeamChallengeProgress) => {
  const tasks = challenge.taskProgress || [];

  const totalTasks = tasks.length;
  const completedTasks = countFullyCompletedTasks(tasks);
  const completedPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  const pointsEarned = sumTaskField(tasks, 'pointsEarned');
  const pointsPossible = sumTaskField(tasks, 'pointsPossible');
  const cluePointPenalty = sumUnrefundedCluePenalty(tasks);
  const totalPoints = sumBy(tasks, getTaskScore);

  const startTime = challenge.startTime ? formatTime(challenge.startTime) : '--';
  const completedTime = challenge.completed ? formatTime(getMostRecentCompletionTime(tasks)) : '--';

  return {
    startTime,
    completedTime,
    completedTasks,
    totalTasks,
    completedPercentage,
    pointsEarned,
    pointsPossible,
    cluePointPenalty,
    totalPoints,
  };
};

const TeamChallengeProgressAnswer: React.FC<TeamChallengeProgressAnswerProps> = ({ challenge }) => {
  const { t } = useTranslation();
  const { loadTeamProgressData } = useJamFacilitator();
  const { jamFacilitatorApi, eventsApi } = useApi();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAWSCLIModal, setShowAWSCLIModal] = useState(false);
  const [confirmModalType, setConfirmModalType] = useState<TEAM_CHALLENGE_CONFIRM_MODAL_TYPE>();
  const [taskId, setTaskId] = useState<string>();
  const [clueOrder, setClueOrder] = useState<number>();
  const [credentials, setCredentials] = useState<AccountCredentials>();
  const [pending, setPending] = useState<boolean>();
  const [restartTime, setRestartTime] = useState<string>();

  const stats = useMemo(() => computeChallengeStats(challenge), [challenge]);
  const {
    startTime,
    completedTime,
    completedTasks,
    totalTasks,
    completedPercentage,
    pointsEarned,
    pointsPossible,
    cluePointPenalty,
    totalPoints,
  } = stats;

  useAsyncEffect(async () => {
    if (challenge.numRestartsUsed <= 0) {
      setRestartTime('--');
      return;
    }

    const labs = await eventsApi.getChallengeLabs(challenge.eventName, challenge.challengeId);
    const mostRecentRestart = getMostRecentRestartTimestamp(labs);
    setRestartTime(formatTime(mostRecentRestart));
  }, [challenge.numRestartsUsed, challenge.eventName, challenge.challengeId, eventsApi]);

  const teamChallengeProgressDropdownItems = useMemo<ButtonDropdownProps.ItemOrGroup[]>(() => {
    const {
      restartChallenge,
      markTaskCompleted,
      downloadSSHKeyPair,
      loginTeamRole,
      loginAdminRole,
      teamRoleCLI,
      adminRoleCLI,
      refundCluePenalty,
    } = i18nKeys.facilitator.participants.buttons;
    const items: ButtonDropdownProps.ItemOrGroup[] = [
      { text: t(restartChallenge) ?? '', id: TEAM_CHALLENGE_DROPDOWN_ITEMS.RESTART_CHALLENGE, iconName: 'redo' },
    ];
    const tasks = sortBy(challenge.taskProgress || [], [({ taskNumber }) => taskNumber]);
    for (const task of tasks) {
      if (!task.completedTime) {
        items.push({
          text: t(markTaskCompleted, { taskNumber: task.taskNumber }) ?? '',
          id: `${TEAM_CHALLENGE_DROPDOWN_ITEMS.MARK_TASK_AS_COMPLETED}#${task.taskId}`,
          iconName: 'thumbs-up-filled',
        });
      }
      for (const cluePenalty of task?.cluePenalties || []) {
        if (!cluePenalty.refunded) {
          items.push({
            text:
              t(refundCluePenalty, {
                taskNumber: task.taskNumber,
                clueOrder: cluePenalty.order,
                penalty: cluePenalty.penalty,
              }) ?? '',
            id: `${TEAM_CHALLENGE_DROPDOWN_ITEMS.REFUND_TASK_CLUE}#${task.taskId}#${cluePenalty.order}`,
            iconName: 'ticket',
          });
        }
      }
    }
    items.push({
      text: t(downloadSSHKeyPair) ?? '',
      id: TEAM_CHALLENGE_DROPDOWN_ITEMS.DOWNLOAD_SSH_KEY_PAIR,
      iconName: 'download',
    });

    if (challenge.teamSignInDetails?.url) {
      items.push({
        text: t(loginTeamRole) ?? '',
        id: TEAM_CHALLENGE_DROPDOWN_ITEMS.LOGIN_AS_TEAM_ROLE,
        iconName: 'external',
      });
    }
    if (challenge.adminSignInDetails?.url) {
      items.push({
        text: t(loginAdminRole) ?? '',
        id: TEAM_CHALLENGE_DROPDOWN_ITEMS.LOGIN_AS_ADMIN_ROLE,
        iconName: 'external',
      });
    }
    // no team credentials when the event owner disabled CLI access for this challenge
    if (challenge.teamSignInDetails?.credentials) {
      items.push({
        text: t(teamRoleCLI) ?? '',
        id: TEAM_CHALLENGE_DROPDOWN_ITEMS.TEAM_ROLE_CLI_ACCESS,
        iconName: 'script',
      });
    }
    if (challenge.adminSignInDetails) {
      items.push({
        text: t(adminRoleCLI) ?? '',
        id: TEAM_CHALLENGE_DROPDOWN_ITEMS.ADMIN_ROLE_CLI_ACCESS,
        iconName: 'script',
      });
    }
    return items;
  }, [t, challenge]);

  const handleItemClick = useCallback<CancelableEventHandler<ButtonDropdownProps.ItemClickDetails>>(
    async (e) => {
      const { detail } = e;
      const [id, currTaskId, currClueOrder] = detail.id.split('#');
      switch (id) {
        case TEAM_CHALLENGE_DROPDOWN_ITEMS.RESTART_CHALLENGE: {
          setShowConfirmModal(true);
          setConfirmModalType(TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.RESTART_CHALLENGE_MODAL);
          break;
        }
        case TEAM_CHALLENGE_DROPDOWN_ITEMS.MARK_TASK_AS_COMPLETED: {
          setShowConfirmModal(true);
          setConfirmModalType(TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.MARK_TASK_COMPLETED);
          setTaskId(currTaskId);
          break;
        }
        case TEAM_CHALLENGE_DROPDOWN_ITEMS.REFUND_TASK_CLUE: {
          setShowConfirmModal(true);
          setConfirmModalType(TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.REFUND_TASK_CLUE);
          setTaskId(currTaskId);
          setClueOrder(parseInt(currClueOrder, 10));
          break;
        }
        case TEAM_CHALLENGE_DROPDOWN_ITEMS.DOWNLOAD_SSH_KEY_PAIR: {
          const blob = await jamFacilitatorApi.downloadSSHKeyPair(
            challenge.eventName,
            challenge.challengeId,
            challenge.teamName
          );
          const text = await blob.text();
          downloadAsPEM(text, 'keypair');
          break;
        }
        case TEAM_CHALLENGE_DROPDOWN_ITEMS.LOGIN_AS_TEAM_ROLE: {
          if (!challenge.teamSignInDetails?.url) return;
          window.open(challenge.teamSignInDetails?.url, '_blank', 'noopener,noreferrer');
          break;
        }
        case TEAM_CHALLENGE_DROPDOWN_ITEMS.LOGIN_AS_ADMIN_ROLE: {
          if (!challenge.adminSignInDetails?.url) return;
          window.open(challenge.adminSignInDetails?.url, '_blank', 'noopener,noreferrer');
          break;
        }
        case TEAM_CHALLENGE_DROPDOWN_ITEMS.TEAM_ROLE_CLI_ACCESS: {
          setShowAWSCLIModal(true);
          setCredentials(challenge.teamSignInDetails?.credentials);
          break;
        }
        case TEAM_CHALLENGE_DROPDOWN_ITEMS.ADMIN_ROLE_CLI_ACCESS: {
          setShowAWSCLIModal(true);
          setCredentials(challenge.adminSignInDetails?.credentials);
          break;
        }
      }
    },
    [challenge, jamFacilitatorApi]
  );

  const handleConfirmAction = useCallback(async () => {
    try {
      setPending(true);
      switch (confirmModalType) {
        case TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.RESTART_CHALLENGE_MODAL: {
          await jamFacilitatorApi.restartChallenge(challenge.eventName, challenge.challengeId, challenge.teamName);
          break;
        }
        case TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.MARK_TASK_COMPLETED: {
          if (!taskId) throw new Error('TaskId not defined');
          await jamFacilitatorApi.markChallengeSolved(
            challenge.eventName,
            challenge.challengeId,
            challenge.teamName,
            taskId
          );
          break;
        }
        case TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.REFUND_TASK_CLUE: {
          if (!taskId) throw new Error('TaskId not defined');
          if (!clueOrder) throw new Error('ClueOrder not defined');
          await jamFacilitatorApi.refundTaskCluePoints(
            challenge.eventName,
            challenge.challengeId,
            challenge.teamName,
            taskId,
            clueOrder
          );
          break;
        }
      }
    } catch (err) {
      preProdLogger(err);
    } finally {
      handleCancelAction();
      void loadTeamProgressData(challenge.eventName, challenge.teamName);
      setPending(false);
    }
  }, [confirmModalType, challenge, taskId, jamFacilitatorApi, clueOrder, loadTeamProgressData]);

  const handleCancelAction = useCallback(() => {
    setConfirmModalType(undefined);
    setShowConfirmModal(false);
    setTaskId(undefined);
    setShowAWSCLIModal(false);
    setCredentials(undefined);
    setClueOrder(undefined);
  }, []);

  const modalMessage = useMemo(() => {
    switch (confirmModalType) {
      case TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.RESTART_CHALLENGE_MODAL: {
        return t(i18nKeys.facilitator.participants.modals.confirmModal.restartChallenge);
      }
      case TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.MARK_TASK_COMPLETED: {
        return t(i18nKeys.facilitator.participants.modals.confirmModal.markTaskCompleted);
      }
      case TEAM_CHALLENGE_CONFIRM_MODAL_TYPE.REFUND_TASK_CLUE: {
        return t(i18nKeys.facilitator.participants.modals.confirmModal.refundClue);
      }
    }
  }, [t, confirmModalType]);
  return (
    <>
      <Container className="dark-bg" data-testid={`team-${challenge.challengeId}-progress`}>
        <SpaceBetween direction="vertical" size="s">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box variant="h3">{challenge.challengeTitle}</Box>
            {teamChallengeProgressDropdownItems &&
              teamChallengeProgressDropdownItems.length > 0 &&
              challenge.startTime !== null && (
                <ButtonDropdown
                  items={teamChallengeProgressDropdownItems}
                  variant="icon"
                  onItemClick={handleItemClick}
                  data-testid={`progress-dropdown-btn`}
                />
              )}
          </div>
          <ColumnLayout borders="horizontal" columns={2}>
            <Box className="space-between">
              <Box>{t(i18nKeys.facilitator.participants.label.startTime)}</Box>
              <Box data-testid="start-time">{startTime}</Box>
            </Box>
            <Box className="space-between">
              <Box>{t(i18nKeys.facilitator.participants.label.completed)}</Box>
              <Box data-testid="challenge-completed">
                {challenge.completed ? t(i18nKeys.general.yes) : t(i18nKeys.general.no)}{' '}
                {`(${
                  completedPercentage > 0 ? completedPercentage.toFixed(2) : completedPercentage
                }%, ${completedTasks}/${totalTasks})`}
              </Box>
            </Box>
            {challenge.completed && (
              <Box className="space-between">
                <Box>{t(i18nKeys.facilitator.participants.label.completedTime)}</Box>
                <Box data-testid="completed-time">{completedTime}</Box>
              </Box>
            )}
            <Box className="space-between">
              <Box>{t(i18nKeys.facilitator.participants.label.pointsEarned)}</Box>
              <Box data-testid="challenge-points-earned">{pointsEarned}</Box>
            </Box>
            <Box className="space-between">
              <Box>{t(i18nKeys.facilitator.participants.label.cluePointPenalty)}</Box>
              <Box data-testid="challenge-clue-point-penalty">{cluePointPenalty}</Box>
            </Box>
            <Box className="space-between">
              <Box>{t(i18nKeys.facilitator.participants.label.totalPoints)}</Box>
              <Box data-testid="challenge-total-points">{`${totalPoints} / ${pointsPossible}`}</Box>
            </Box>
            <Box className="space-between">
              <Box>{t(i18nKeys.facilitator.participants.label.restartUsed)}</Box>
              <Box data-testid="restarts-count">{challenge.numRestartsUsed ?? 0}</Box>
            </Box>
            {challenge.numRestartsUsed > 0 && (
              <Box className="space-between">
                <Box>{t(i18nKeys.facilitator.participants.label.restartTime)}</Box>
                <Box data-testid="restarts-time">{restartTime}</Box>
              </Box>
            )}
          </ColumnLayout>
          <ShowTasks taskProgress={challenge?.taskProgress ?? []} />
        </SpaceBetween>
      </Container>
      <ConfirmModal
        message={modalMessage}
        visible={showConfirmModal}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        data-testid={`progress-confirm-modal`}
        loading={pending}
      />
      {credentials && <AwsCliModal value={showAWSCLIModal} setValue={setShowAWSCLIModal} awsAccount={credentials} />}
    </>
  );
};

interface TeamProgressProps {
  teamProgress: TeamProgressResponse | undefined;
}

const TeamProgress: React.FC<TeamProgressProps> = ({ teamProgress }) => {
  const { t } = useTranslation();
  const answers = teamProgress?.teamChallengeAnswers ?? [];
  const firstAnswer = answers[0];

  const challenges = useMemo<LeaderboardAdjustmentChallenge[]>(
    () =>
      answers.map(({ challengeId, challengeTitle }) => ({
        challengeId,
        challengeTitle: challengeTitle ?? challengeId,
      })),
    [answers]
  );

  return (
    <SpaceBetween direction="vertical" size="s">
      {config.ff.enableFacilitatorTools && firstAnswer && (
        <>
          <LeaderboardAdjustments
            eventName={firstAnswer.eventName}
            teamName={firstAnswer.teamName}
            adjustment={teamProgress?.leaderboardAdjustment}
            challenges={challenges}
          />
          {answers.length > 0 && (
            <Header variant="h3">{t(i18nKeys.facilitator.participants.label.challengeProgress)}</Header>
          )}
        </>
      )}
      {answers.map((challenge, i) => (
        <TeamChallengeProgressAnswer key={i} challenge={challenge} />
      ))}
    </SpaceBetween>
  );
};

export default TeamProgress;
